import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Helmet } from "react-helmet";
import { useToast } from "@/components/ui/use-toast";
import { AuthLayout } from "@/components/auth/AuthLayout";
import { SignInForm } from "@/components/auth/SignInForm";
import { MagicLinkForm } from "@/components/auth/MagicLinkForm";
import { OnboardingModal } from "@/components/onboarding/OnboardingModal";

const SignIn = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isMagicLink, setIsMagicLink] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);

  useEffect(() => {
    const checkSession = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          // Check if profile exists
          const { data: profile, error: profileError } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', session.user.id)
            .single();

          if (profileError) {
            if (profileError.code === 'PGRST116') {
              // Profile doesn't exist, show onboarding
              setShowOnboarding(true);
            } else {
              console.error('Error fetching profile:', profileError);
              toast({
                title: "Error",
                description: "There was an error loading your profile. Please try again.",
                variant: "destructive",
              });
            }
          } else if (!profile.full_name || !profile.phone) {
            setShowOnboarding(true);
          } else {
            navigate('/dashboard');
          }
        }
      } catch (error) {
        console.error('Error in checkSession:', error);
        toast({
          title: "Error",
          description: "There was an error checking your session. Please try again.",
          variant: "destructive",
        });
      }
    };

    checkSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_IN' && session) {
        try {
          // Check if profile exists
          const { data: profile, error: profileError } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', session.user.id)
            .single();

          if (profileError) {
            if (profileError.code === 'PGRST116') {
              // Profile doesn't exist, show onboarding
              setShowOnboarding(true);
            } else {
              throw profileError;
            }
          } else if (!profile.full_name || !profile.phone) {
            setShowOnboarding(true);
          } else {
            navigate('/dashboard');
          }
        } catch (error) {
          console.error('Error in auth state change:', error);
          toast({
            title: "Error",
            description: "There was an error loading your profile. Please try again.",
            variant: "destructive",
          });
        }
      } else if (event === 'SIGNED_OUT') {
        navigate('/');
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [navigate, toast]);

  return (
    <>
      <Helmet>
        <title>Sign In - Numberz Insight | Professional Accounting Services</title>
        <meta 
          name="description" 
          content="Sign in to your Numberz Insight account to access professional accounting and business registration services." 
        />
      </Helmet>
      
      <AuthLayout
        title="Welcome back"
        subtitle="Sign in to access your account"
      >
        {isMagicLink ? (
          <MagicLinkForm onPasswordClick={() => setIsMagicLink(false)} />
        ) : (
          <SignInForm onMagicLinkClick={() => setIsMagicLink(true)} />
        )}
      </AuthLayout>

      <OnboardingModal 
        isOpen={showOnboarding} 
        onClose={() => {
          setShowOnboarding(false);
          navigate('/dashboard');
        }} 
      />
    </>
  );
};

export default SignIn;